import { listeningApiChapterDtoToDocReader } from '@listening/api-contract/legacy-client-types';
import type { LegacyChapterChunkParams } from '@listening/frontend/legacy-reader/chapter-chunks';
import { makeLegacyChapterChunkReader } from '@listening/frontend/legacy-reader/use-legacy-chapter-chunks';
import { listLegacyChapterChunkEffect } from '@listening/frontend/query/chapters';
import { resolveAtom } from '@listening/frontend/query/load-atom';
import { Duration, Effect, Schema } from 'effect';
import { Atom, AtomRegistry } from 'effect/reactivity';

export const legacyChapterChunkKey = (params: LegacyChapterChunkParams) =>
  `${params.audioId}:${params.cursor}:${params.limit.toString()}`;

/**
 * The legacy chunks this tab holds, so search and the reader's index space can
 * read them without fetching any. It travels with the server-rendered page, so
 * the chunk the public doc page renders counts once it hydrates.
 */
export const loadedLegacyChapterChunksAtom = Atom.make<ReadonlyArray<string>>(
  [],
).pipe(
  Atom.serializable({
    key: 'legacy-chapter-chunks-loaded',
    schema: Schema.Array(Schema.String),
  }),
  Atom.keepAlive,
);

const recordLoadedChunk = (params: LegacyChapterChunkParams) =>
  Effect.gen(function* () {
    const registry = yield* AtomRegistry.AtomRegistry;
    const key = legacyChapterChunkKey(params);
    registry.update(loadedLegacyChapterChunksAtom, (keys) => [...keys, key]);
    yield* Effect.addFinalizer(() =>
      Effect.sync(() =>
        registry.update(loadedLegacyChapterChunksAtom, (keys) =>
          keys.filter((loaded) => loaded !== key),
        ),
      ),
    );
  });

/**
 * The web reader's legacy chapter text. A chunk the reader no longer shows is
 * dropped ten minutes after it closes.
 */
export const legacyChapterChunkReader = makeLegacyChapterChunkReader({
  fetchChunk: (params) =>
    listLegacyChapterChunkEffect(params).pipe(
      Effect.tap(() => recordLoadedChunk(params)),
      Effect.tapError((error) =>
        Effect.logError('Error fetching chapters chunk', { params, error }),
      ),
    ),
  toPage: listeningApiChapterDtoToDocReader,
  timeToLive: Duration.minutes(10),
});

/** A chunk's chapters, fetching the chunk if it has not loaded. */
export const loadLegacyChapterChunk = (
  registry: AtomRegistry.AtomRegistry,
  params: LegacyChapterChunkParams,
) =>
  Effect.runPromise(
    resolveAtom(registry, legacyChapterChunkReader.chunkAtom(params)),
  );
